import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Layers, Tags, Boxes, SlidersHorizontal, ArrowRight, Check, X } from "lucide-react";

/**
 * CoachMarks — one-time first-launch tour.
 *
 * Cold launch (no `cdi:first_run_completed` key): mounts as a portal
 * overlay walking the user through the CDI hierarchy — Collections →
 * Categories → Items. Skip / Next / Got it all set the flag so warm
 * launches never re-show the tour.
 *
 * Kept self-contained: no props, no routing, no data fetches.
 */
const STORAGE_KEY = "cdi:first_run_completed";

// P-suppress: routes where the coach-marks modal must NEVER render, even
// on cold launch. Matches the Edit Item form so the fixed portal overlay
// cannot intercept clicks or block the Save button on returning-user
// visits. Other routes keep the existing first-run behaviour.
const SUPPRESSED_PATH_RE = /^\/(items\/[^/]+\/edit|admin)(\/.*)?$/;
const isSuppressedPath = () => {
  if (typeof window === "undefined") return false;
  try { return SUPPRESSED_PATH_RE.test(window.location.pathname || ""); }
  catch { return false; }
};

const STEPS = [
  {
    icon: SlidersHorizontal,
    title: "Set Your Currency & Tax Rate",
    body: "Before adding collections, open Settings and set your preferred currency and your country\u2019s tax rate. The app uses these values to convert EX/INC pricing and calculate totals accurately.",
  },
  {
    icon: Layers,
    title: "Start with a Collection",
    body: "A Collection is the top level — think 'My Wine Cellar' or 'Whisky Vault'. It sets the base template for the items inside.",
  },
  {
    icon: Tags,
    title: "Group with Categories",
    body: "Inside each Collection, Categories help you slice things by region, style, or your own labels. CDI's master fields are locked to keep data clean.",
  },
  {
    icon: Boxes,
    title: "Add Items",
    body: "Log each collectable as an item.",
  },
];

export default function CoachMarks() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Never trigger the tour when the current route is the Edit Item
    // form — the modal's fixed portal overlay would intercept clicks
    // and prevent Save. All other routes keep normal cold-launch
    // behaviour.
    if (isSuppressedPath()) return;
    try {
      const done = window.localStorage.getItem(STORAGE_KEY);
      if (!done) setOpen(true);
    } catch { /* private mode — treat as done to avoid nagging */ }
  }, []);

  // P — Manual "Show tour again" trigger from Settings. Settings clears
  // the storage flag and dispatches this event; we re-open the tour
  // here from step 0, but still honour the Edit Item suppression so
  // the user isn't dropped into a locked form.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onRestart = () => {
      if (isSuppressedPath()) return;
      setStep(0);
      setOpen(true);
    };
    window.addEventListener("cdi:restart-coach-marks", onRestart);
    return () => window.removeEventListener("cdi:restart-coach-marks", onRestart);
  }, []);

  const finish = () => {
    try { window.localStorage.setItem(STORAGE_KEY, "true"); } catch { /* best-effort */ }
    setOpen(false);
  };

  // Belt-and-braces: even if the modal was already open from a prior
  // route (e.g. cold-launch on /items → user navigates to Edit), suppress
  // the render on the Edit Item route so nothing overlays the form.
  if (!open || isSuppressedPath()) return null;

  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  const modal = (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm vp-fade-in"
      data-testid="coach-marks"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coach-marks-title"
    >
      <div className="w-full sm:max-w-md bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-t-2xl sm:rounded-lg shadow-xl flex flex-col">
        <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-wider vp-muted">
            Quick tour · Step {step + 1} of {STEPS.length}
          </div>
          <button
            type="button"
            onClick={finish}
            data-testid="coach-marks-skip"
            aria-label="Skip tour"
            className="vp-btn vp-btn-ghost vp-btn-sm !w-8 !h-8 !p-0 border border-[hsl(var(--border))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-5 py-6 text-center">
          <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] flex items-center justify-center">
            <Icon size={26} strokeWidth={1.75} />
          </div>
          <h2
            id="coach-marks-title"
            data-testid="coach-marks-title"
            className="text-lg font-semibold tracking-tight mb-2"
          >
            {s.title}
          </h2>
          <p className="text-sm vp-muted leading-relaxed max-w-sm mx-auto">
            {s.body}
          </p>

          {/* Step pips */}
          <div className="flex items-center justify-center gap-1.5 mt-5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={
                  "block h-1.5 rounded-full transition-all " +
                  (i === step
                    ? "w-6 bg-[hsl(var(--gold))]"
                    : "w-1.5 bg-[hsl(var(--border))]")
                }
              />
            ))}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={finish}
            data-testid="coach-marks-skip-btn"
            className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
          >
            Skip
          </button>
          {isLast ? (
            <button
              type="button"
              onClick={finish}
              data-testid="coach-marks-done"
              className="vp-btn vp-btn-primary vp-btn-sm"
            >
              <Check size={13} /> Got it
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((n) => Math.min(n + 1, STEPS.length - 1))}
              data-testid="coach-marks-next"
              className="vp-btn vp-btn-primary vp-btn-sm"
            >
              Next <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}
